import React, { useEffect, useRef } from 'react';
import * as ZXingBrowser from '@zxing/browser';
import * as ZXing from '@zxing/library';
import { F } from '../theme.jsx';
import { ModalCloseButton } from '../modal-utils.jsx';

// ─────────────────────────────────────────────────────────────
// Tracking number extraction from shipping labels (client-side)
//
// Pipeline:
//   1. PDF → render first page to canvas via pdf.js
//      Image (PNG/JPEG/WEBP) → decode to canvas
//   2. Canvas → ZXing barcode decoder (Code128, QR, DataMatrix, …)
//   3. If no barcode → lazy-load Tesseract.js → OCR canvas → regex match
//   4. Sanitize result (uppercase, alphanumeric, max 50 chars)
//
// Everything runs in the browser. No data sent to extraction servers.
// ─────────────────────────────────────────────────────────────

// Tracking-Pattern-Stack: hochspezifische Carrier-Formate ZUERST,
// generische Ziffern-Patterns nur als allerletzter Fallback. Jeder
// Treffer wird gegen Phone- und sonstige False-Positive-Heuristiken geprüft.
const TRACKING_PATTERNS = [
  // DHL international ("RR123456789DE", "LX012345678CN") — sehr spezifisch
  { re: /\b[A-Z]{2}\d{9,11}[A-Z]{2}\b/, weight: 100, name: "intl" },
  // DHL Paket DE (20 Ziffern)
  { re: /\b\d{20}\b/, weight: 95, name: "dhl20" },
  // DHL Express (10-12 Ziffern) — zwischen Paket und kurz
  { re: /\b\d{12}\b/, weight: 50, name: "dhl12" },
  // Hermes ("H" + 14-20 Ziffern)
  { re: /\bH\d{14,20}\b/, weight: 90, name: "hermes" },
  // UPS ("1Z" + 16 alphanumeric)
  { re: /\b1Z[A-Z0-9]{16}\b/, weight: 100, name: "ups" },
  // DPD (14 Ziffern)
  { re: /\b\d{14}\b/, weight: 70, name: "dpd14" },
  // GLS (11 Ziffern)
  { re: /\b\d{11}\b/, weight: 35, name: "gls11" },
  // FedEx (15 Ziffern)
  { re: /\b\d{15}\b/, weight: 70, name: "fedex" },
  // Alphanumeric mit min. 1 Buchstabe (kein Phone)
  { re: /\b(?=[A-Z0-9]*[A-Z])[A-Z0-9]{12,30}\b/, weight: 60, name: "alpha" },
];

// Tracking-Keyword-Match: NUR spezifische Keywords ("Sendungsnr", "Tracking", "AWB").
// "Paket" allein ist zu generisch — eingeschränkt auf "Paketnr" / "Paketnummer".
// Capture-Group nimmt NUR alphanumeric + `-` (KEINE Spaces) damit "Max Mustermann"
// nicht fälschlich als Tracking durchrutscht.
const TRACKING_KEYWORD_RE = /(?:tracking[- ]?(?:nr|number)|sendung(?:sverfolgung|snr|snummer|s-?nr)|paket(?:nr|nummer)|versand(?:nr|nummer)|shipment[- ]?(?:nr|number)|consignment[- ]?(?:nr|number)|awb)\s*[:#-]?\s*([A-Z0-9][A-Z0-9-]{7,40})/i;

// Phone-Number-Patterns — Phone-Nummern haben IMMER einen Separator
// (Leerzeichen, Bindestrich, Slash). Lange Ziffernketten ohne Separator
// sind KEINE Telefon-Nummern (z.B. DHL 003404342...).
const PHONE_PATTERNS = [
  /\+\s*\d{1,3}[\s\-/]\d/,            // International prefix "+49 ..."
  /\b00\d{2,3}[\s\-/]\d/,             // Deutscher Auslandspräfix "0049 ..." nur MIT Separator
  /\b0\d{2,4}[\s\-/]\d/,              // Deutsche Vorwahl "030 ..." / "0151-..."
  /\(\s*0?\d+\s*\)\s*\d/,             // "(030) ..."
];
const PHONE_KEYWORD_RE = /(?:^|\s)(?:tel(?:efon)?|phone|fon|mobil|mobile|fax|hotline)\s*[:.]?\s*$/i;

// Invoice/Order/Customer-Keywords — wenn unmittelbar VOR einem numerischen
// Match auf der gleichen Zeile, ist die Nummer höchstwahrscheinlich eine
// Rechnungs-/Bestell-/Kundennummer und KEINE Trackingnummer.
const INVOICE_KEYWORD_RE = /(?:^|\s)(?:rechnung(?:s)?(?:nr|nummer)?|bestell(?:nr|nummer|ung)?|order(?:[- ]?(?:id|nr|number))?|invoice(?:[- ]?(?:nr|number))?|kunden(?:[- ]?(?:nr|nummer))?|customer(?:[- ]?(?:id|nr))?|reference|referenz|auftrag(?:s)?(?:nr|nummer)?|art(?:ikel)?[- ]?nr|sku|ust[- ]?id|steuer)\s*[:.#-]?\s*$/i;

function sanitizeTrackingNumber(raw) {
  if (!raw) return null;
  const cleaned = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 50);
  if (cleaned.length < 8) return null;
  return cleaned;
}

// False-Positive-Kontext-Check: ist der Match
//   (a) Teil einer Telefonnummer auf gleicher Zeile, oder
//   (b) direkt nach einem Phone-Keyword ("Tel:", "Fax:"), oder
//   (c) direkt nach einem Invoice-/Order-/Kunden-Keyword ("Rechnungsnr:", "Bestellnr:", "Kunden-Nr:"...)?
// Wenn ja → Match wird NICHT als Trackingnummer akzeptiert.
function looksLikeFalsePositiveInContext(text, matchIdx, matchLen) {
  // Zeilen-Grenzen
  const lineStart = text.lastIndexOf("\n", matchIdx - 1) + 1;
  const lineEndN  = text.indexOf("\n", matchIdx + matchLen);
  const lineEnd   = lineEndN === -1 ? text.length : lineEndN;
  const line = text.slice(lineStart, lineEnd);
  const matchPosInLine = matchIdx - lineStart;
  const before = line.slice(0, matchPosInLine);

  // 1. Phone-Pattern überlappt mit dem Match-Bereich auf gleicher Zeile?
  for (const re of PHONE_PATTERNS) {
    const lineRe = new RegExp(re.source, "g");
    let pm;
    while ((pm = lineRe.exec(line)) !== null) {
      const phoneStart = pm.index;
      const phoneEnd   = pm.index + pm[0].length;
      if (phoneStart <= matchPosInLine + matchLen && phoneEnd >= matchPosInLine) return true;
    }
  }

  // 2. Direkt davor "Tel:" / "Phone:" / "Fax:" etc.?
  if (PHONE_KEYWORD_RE.test(before)) return true;

  // 3. Direkt davor "Rechnungsnr:" / "Bestellung:" / "Kunden-Nr:" etc.?
  if (INVOICE_KEYWORD_RE.test(before)) return true;

  return false;
}

// Returns the top-1 candidate or null. Backwards-compat wrapper.
function matchTrackingPattern(text) {
  const cs = matchTrackingPatternCandidates(text, 1);
  return cs[0]?.value || null;
}

// Returns up to maxCount distinct candidates sorted by score desc.
// Each candidate: { value, score, name, sourceText }
function matchTrackingPatternCandidates(text, maxCount = 5) {
  if (!text) return [];
  const upper = text.toUpperCase();
  const seen = new Set();
  const out = [];

  function push(value, score, name, sourceText) {
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push({ value, score, name, sourceText });
  }

  // 1. Höchste Priorität: spezifischer Tracking-Keyword + sanitized check
  const kw = text.match(TRACKING_KEYWORD_RE);
  if (kw && kw[1]) {
    const sanitized = sanitizeTrackingNumber(kw[1]);
    if (sanitized && sanitized.length >= 8 && /\d/.test(sanitized)) {
      push(sanitized, 200, "keyword", kw[0]);
    }
  }

  // 2. Pattern-Treffer sammeln und scoren
  for (const { re, weight, name } of TRACKING_PATTERNS) {
    const globalRe = new RegExp(re.source, "g");
    let m;
    while ((m = globalRe.exec(upper)) !== null) {
      const matched = m[0];
      const idx = m.index;
      if (looksLikeFalsePositiveInContext(text, idx, matched.length)) continue;
      const sanitized = sanitizeTrackingNumber(matched);
      if (!sanitized || !/\d/.test(sanitized)) continue;
      const score = weight + Math.min(20, sanitized.length);
      push(sanitized, score, name, matched);
    }
  }

  out.sort((a, b) => b.score - a.score);
  return out.slice(0, maxCount);
}

// Strenger Validator: prüft ob ein sanitized String EXAKT einem bekannten
// Carrier-Format entspricht. Nutzt nur die spezifischen Carrier-Patterns
// (NICHT das generische "alpha"-Catchall) — das schützt vor False-Positives
// bei Kunden-Referenzen, URLs, beliebigen IDs.
//
// Wird benutzt von:
//   - extractTrackingNumber Stage-1 (akzeptiert Barcode nur wenn Tracking-Format)
//   - handleScanResult (silent-skip bei Nicht-Tracking-Codes)
const STRICT_TRACKING_PATTERNS = TRACKING_PATTERNS.filter(p => p.name !== "alpha");

function looksLikeValidTrackingNumber(sanitized) {
  if (!sanitized || sanitized.length < 8) return false;
  // Tracking braucht IMMER mindestens eine Ziffer
  if (!/\d/.test(sanitized)) return false;
  for (const { re } of STRICT_TRACKING_PATTERNS) {
    // \b…\b in der Pattern-Source entfernen, dann ^…$ wrappen für vollständige Match-Prüfung
    const inner = re.source.replace(/^\\b/, "").replace(/\\b$/, "");
    const fullMatch = new RegExp("^(?:" + inner + ")$");
    if (fullMatch.test(sanitized)) return true;
  }
  return false;
}

// Render a File (PDF or image) to an offscreen canvas. Returns canvas + raster size.
async function fileToCanvas(file) {
  const type = (file.type || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  const isPdf = type.includes("pdf") || name.endsWith(".pdf");

  if (isPdf) {
    // Lazy-load pdf.js ESM module
    const pdfjs = await import("https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.min.mjs");
    if (window.__PDFJS_WORKER_SRC__) {
      pdfjs.GlobalWorkerOptions.workerSrc = window.__PDFJS_WORKER_SRC__;
    }
    const buf = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: buf }).promise;
    const page = await pdf.getPage(1);
    // Render at 2x scale for better OCR/barcode legibility
    const viewport = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    await page.render({ canvasContext: ctx, viewport }).promise;
    return canvas;
  }

  // Image path
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Scan a canvas across multiple regions to find all unique barcodes.
// Shipping labels often have multiple barcodes (tracking Code128 + QR URL).
// Tries full canvas first, then halves to pick up barcodes in different regions.
// Returns array of unique decoded text values in order of discovery.
async function scanAllBarcodesFromCanvas(canvas) {
  const seen = new Set();
  const results = [];

  async function tryCanvas(c) {
    const text = await scanBarcodeFromCanvas(c);
    if (text && !seen.has(text)) { seen.add(text); results.push(text); }
  }

  await tryCanvas(canvas); // full image first

  const regions = [
    [0, 0, 1, 0.5],   // top half
    [0, 0.5, 1, 1],   // bottom half
    [0, 0, 0.5, 1],   // left half
    [0.5, 0, 1, 1],   // right half
  ];
  for (const [x1, y1, x2, y2] of regions) {
    const w = Math.round((x2 - x1) * canvas.width);
    const h = Math.round((y2 - y1) * canvas.height);
    if (w < 50 || h < 50) continue;
    const crop = document.createElement("canvas");
    crop.width = w; crop.height = h;
    crop.getContext("2d").drawImage(
      canvas,
      Math.round(x1 * canvas.width), Math.round(y1 * canvas.height), w, h,
      0, 0, w, h
    );
    await tryCanvas(crop);
  }
  return results;
}

// Try to read any barcode on the canvas using ZXing.
// Returns the decoded text, or null if nothing found.
async function scanBarcodeFromCanvas(canvas) {
  if (!ZXingBrowser?.BrowserMultiFormatReader) return null;
  try {
    // Multi-format reader for shipping labels: Code128, QR, DataMatrix, EAN, ITF
    const hints = new Map();
    if (typeof ZXing !== "undefined" && ZXing.DecodeHintType && ZXing.BarcodeFormat) {
      hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, [
        ZXing.BarcodeFormat.CODE_128,
        ZXing.BarcodeFormat.CODE_39,
        ZXing.BarcodeFormat.QR_CODE,
        ZXing.BarcodeFormat.DATA_MATRIX,
        ZXing.BarcodeFormat.EAN_13,
        ZXing.BarcodeFormat.ITF,
        ZXing.BarcodeFormat.PDF_417,
      ]);
      hints.set(ZXing.DecodeHintType.TRY_HARDER, true);
    }
    const reader = new ZXingBrowser.BrowserMultiFormatReader(hints);
    const result = await reader.decodeFromCanvas(canvas);
    return result?.getText?.() || null;
  } catch (e) {
    // ZXing throws NotFoundException when nothing recognized — that's fine
    return null;
  }
}

// Lazy-load Tesseract.js and run OCR. Returns extracted text or null.
let _tesseractPromise = null;
async function loadTesseract() {
  if (_tesseractPromise) return _tesseractPromise;
  _tesseractPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist/tesseract.min.js";
    s.onload = () => resolve(window.Tesseract);
    s.onerror = () => { _tesseractPromise = null; reject(new Error("Tesseract load failed")); };
    document.head.appendChild(s);
  });
  return _tesseractPromise;
}

async function ocrCanvas(canvas) {
  try {
    const Tesseract = await loadTesseract();
    if (!Tesseract) return null;
    // Quick OCR (~3-6s for a label). German + English language.
    const { data } = await Tesseract.recognize(canvas, "eng+deu");
    return data?.text || null;
  } catch (e) {
    console.warn("OCR failed:", e?.message);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// Audio feedback (Web Audio API, no external assets)
// ─────────────────────────────────────────────────────────────
let _audioCtx = null;
function getAudioCtx() {
  if (_audioCtx) return _audioCtx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    _audioCtx = new Ctor();
    return _audioCtx;
  } catch { return null; }
}
export function playBeep(type = "success") {
  const ctx = getAudioCtx();
  if (!ctx) return;
  // Some browsers require resume() after a user gesture
  if (ctx.state === "suspended") ctx.resume();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  if (type === "success") {
    osc.frequency.value = 880;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.10);
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
  } else {
    // error: lower buzzy tone
    osc.frequency.value = 220;
    osc.type = "square";
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
    osc.start();
    osc.stop(ctx.currentTime + 0.24);
  }
}

// ─────────────────────────────────────────────────────────────
// Order matching for scanned barcodes
// Restricts to "Bereit" orders (workflow constraint).
// Match strategies (in order): exact → substring beidseitig.
// Returns array of matching orders (0, 1, or many).
// ─────────────────────────────────────────────────────────────
export function findOrdersForCode(code, allOrders) {
  if (!code || !Array.isArray(allOrders)) return [];
  const sanitized = sanitizeTrackingNumber(code);
  if (!sanitized) return [];

  const candidates = allOrders.filter(o => o.status === "Bereit" && !o.archived);

  // Stage 1: exact tracking number match
  const exact = candidates.filter(o => {
    const t = sanitizeTrackingNumber(o.trackingNumber || "");
    return t && t === sanitized;
  });
  if (exact.length > 0) return exact;

  // Stage 2: substring (label barcodes often embed extra data around the tracking)
  const substr = candidates.filter(o => {
    const t = sanitizeTrackingNumber(o.trackingNumber || "");
    if (!t || t.length < 8) return false;
    return sanitized.includes(t) || t.includes(sanitized);
  });
  return substr;
}

// Top-level: extract tracking number from a shipping label file.
// Returns { value, source } or null.
// Extrahiert eine Tracking-Nummer aus einem Versand-Label.
// Returns: { primary, candidates, ocrText, source } | null
//   - primary: best-guess sanitized value (string) or null
//   - candidates: Array von { value, score, name, sourceText } (max 5, sorted by score)
//   - ocrText: roher OCR-Text (für Debug + manuelle Korrektur durch User)
//   - source: "barcode" (auto-akzeptabel) | "ocr" (User-Bestätigung empfohlen) | null
export async function extractTrackingNumber(file) {
  let canvas;
  try {
    canvas = await fileToCanvas(file);
  } catch (e) {
    console.warn("Could not render file:", e?.message);
    return null;
  }

  // Stage 1 — multi-barcode scan (fast path, ~99% of modern labels)
  // Scans the full canvas plus 4 half-regions to find all barcodes on the label.
  // Prefers barcodes matching a known carrier format; otherwise takes the first
  // valid sanitized value. Falls through to OCR only when no usable barcode found.
  const barcodes = await scanAllBarcodesFromCanvas(canvas);
  if (barcodes.length > 0) {
    const sanitized = barcodes.map(b => sanitizeTrackingNumber(b)).filter(Boolean);
    if (sanitized.length > 0) {
      // Prefer a value that matches a known carrier pattern (avoids QR-code URLs etc.)
      const best = sanitized.find(s => looksLikeValidTrackingNumber(s)) ?? sanitized[0];
      const uniqueSanitized = [...new Set(sanitized)];
      return {
        primary: best,
        candidates: uniqueSanitized.map(v => ({
          value: v,
          score: looksLikeValidTrackingNumber(v) ? 999 : 500,
          name: "barcode",
          sourceText: barcodes[sanitized.indexOf(v)] ?? v,
        })),
        ocrText: "",
        source: "barcode",
      };
    }
    // All barcodes failed sanitization (too short etc.) → fall through to OCR
  }

  // Stage 2 — OCR fallback (mit Phone- + Invoice-Filter)
  const text = await ocrCanvas(canvas);

  const candidates = matchTrackingPatternCandidates(text || "", 5);

  if (candidates.length === 0) {
    return { primary: null, candidates: [], ocrText: text || "", source: "ocr" };
  }
  return {
    primary: candidates[0].value,
    candidates,
    ocrText: text || "",
    source: "ocr",
  };
}


export function ScannerModal({
  t, dark, F,
  bereitOrders,
  scannerState, scannerError,
  lastMatchedOrder, lastUnmatchedCode,
  onClose, onScanResult, onSetState, onError,
  scannerControlsRef,
  getStatusLabel, getChannelLabel,
  extractTrackingNumber,
}) {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  // Start the ZXing reader once the modal mounts and the <video> ref is ready
  useEffect(() => {
    let cancelled = false;
    async function start() {
      // No camera API at all → fallback only
      if (!navigator.mediaDevices?.getUserMedia) {
        onError("Kamera in diesem Browser nicht verfügbar");
        onSetState("error");
        return;
      }
      if (!ZXingBrowser?.BrowserMultiFormatReader) {
        onError("Scanner-Bibliothek nicht geladen — bitte Seite neu laden");
        onSetState("error");
        return;
      }
      try {
        onSetState("scanning");
        // Configure decoder with shipping-label barcode formats (focused = faster)
        const hints = new Map();
        if (typeof ZXing !== "undefined" && ZXing.DecodeHintType && ZXing.BarcodeFormat) {
          hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, [
            ZXing.BarcodeFormat.CODE_128,
            ZXing.BarcodeFormat.CODE_39,
            ZXing.BarcodeFormat.QR_CODE,
            ZXing.BarcodeFormat.DATA_MATRIX,
            ZXing.BarcodeFormat.EAN_13,
            ZXing.BarcodeFormat.PDF_417,
            ZXing.BarcodeFormat.ITF,
          ]);
        }
        const reader = new ZXingBrowser.BrowserMultiFormatReader(hints);
        // Prefer the rear camera
        const constraints = {
          video: {
            facingMode: { ideal: "environment" },
            width:  { ideal: 1280 },
            height: { ideal: 720 },
          },
        };
        const controls = await reader.decodeFromConstraints(
          constraints,
          videoRef.current,
          (result, err) => {
            if (cancelled) return;
            if (result) onScanResult(result.getText());
            // Errors during scanning are normal (NotFoundException between frames) — ignore
          }
        );
        if (cancelled) {
          try { controls.stop(); } catch {}
          return;
        }
        scannerControlsRef.current = controls;
      } catch (e) {
        if (cancelled) return;
        const msg = String(e?.message || e || "");
        if (/permission|NotAllowed|denied/i.test(msg)) {
          onError("Kamera-Zugriff verweigert");
        } else if (/NotFound|no .* device/i.test(msg)) {
          onError("Keine Kamera gefunden");
        } else {
          onError("Kamera konnte nicht gestartet werden");
        }
        onSetState("error");
      }
    }
    start();
    return () => {
      cancelled = true;
      try { scannerControlsRef.current?.stop?.(); } catch {}
      scannerControlsRef.current = null;
    };
  }, []);

  // Manual photo fallback (no camera permission) — runs the same extraction pipeline
  async function handleManualPhoto(file) {
    if (!file) return;
    onSetState("scanning");
    try {
      const result = await extractTrackingNumber(file);
      if (result?.primary) {
        onScanResult(result.primary);
      } else {
        // Trigger no_match overlay manually with the failure
        onScanResult("");
      }
    } catch {
      onScanResult("");
    }
  }

  return (
    <>
      {/* Fullscreen black backdrop */}
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "#000", zIndex: 100,
      }} />

      {/* Modal content */}
      <div onClick={e => e.stopPropagation()} style={{
        position: "fixed", inset: 0, zIndex: 101,
        display: "flex", flexDirection: "column",
        fontFamily: F, color: "#fff",
      }}>
        {/* Header */}
        <div style={{
          padding: "calc(env(safe-area-inset-top, 0px) + 14px) 16px 14px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "rgba(0,0,0,0.7)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}>
          <ModalCloseButton onClose={onClose} dark={true} variant="onDark" />
          <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.2px" }}>Scannen</div>
          <div style={{ width: 30 }}/>
        </div>

        {/* Camera viewport */}
        <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "#000" }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />

          {/* Scan box overlay (visual guide) */}
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            pointerEvents: "none",
          }}>
            <div style={{
              width: "min(80%, 360px)", aspectRatio: "1.6 / 1",
              border: "2px solid rgba(255,255,255,0.6)",
              borderRadius: 18,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
            }} />
          </div>

          {/* Match overlay (success) */}
          {scannerState === "match" && lastMatchedOrder && (
            <div style={{
              position: "absolute", left: 16, right: 16, top: 24,
              padding: "16px 18px", borderRadius: 18,
              background: "rgba(48,209,88,0.95)",
              boxShadow: "0 6px 24px rgba(48,209,88,0.4)",
              animation: "modalIn 0.18s cubic-bezier(0.34,1.4,0.64,1)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 22,
                  background: "rgba(255,255,255,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <path d="M3 11.5L8.5 17L19 5" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", letterSpacing: "-0.2px" }}>
                    #{lastMatchedOrder.bestellnummer || "—"}
                  </div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.9)", marginTop: 2 }}>
                    {getChannelLabel(lastMatchedOrder.kanal)} · als {getStatusLabel("Versendet")} markiert
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No-match overlay (error) */}
          {scannerState === "no_match" && (
            <div style={{
              position: "absolute", left: 16, right: 16, top: 24,
              padding: "16px 18px", borderRadius: 18,
              background: "rgba(255,69,58,0.95)",
              boxShadow: "0 6px 24px rgba(255,69,58,0.4)",
              animation: "modalIn 0.18s cubic-bezier(0.34,1.4,0.64,1)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 22,
                  background: "rgba(255,255,255,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <path d="M4 4L18 18M18 4L4 18" stroke="#fff" strokeWidth="3" strokeLinecap="round"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", letterSpacing: "-0.2px" }}>
                    Kein Treffer
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.9)", marginTop: 2, fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {lastUnmatchedCode || "Code nicht erkannt"}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error state — camera permission denied / unavailable */}
          {scannerState === "error" && (
            <div style={{
              position: "absolute", inset: 0, background: "rgba(0,0,0,0.85)",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              padding: 28, textAlign: "center",
            }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>📷</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
                {scannerError || "Kamera nicht verfügbar"}
              </div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 22, lineHeight: 1.5, maxWidth: 320 }}>
                Du kannst auch ein Foto vom Versandlabel aufnehmen — die Trackingnummer wird automatisch erkannt.
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleManualPhoto(f); e.target.value = ""; }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  background: "var(--accent-online)", border: "none", borderRadius: 14,
                  padding: "13px 22px", color: "#fff", fontFamily: F,
                  fontSize: 15, fontWeight: 600, cursor: "pointer",
                  boxShadow: "0 4px 18px rgba(55,138,221,0.35)",
                }}
              >📸 Foto aufnehmen</button>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div style={{
          padding: "14px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)",
          background: "rgba(0,0,0,0.7)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", marginBottom: 4 }}>
            Halte den Barcode in den Rahmen
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
            {bereitOrders.length === 0
              ? "Keine offenen Bestellungen"
              : bereitOrders.length === 1
                ? "1 Bestellung scannbar"
                : `${bereitOrders.length} Bestellungen scannbar`}
          </div>
        </div>
      </div>
    </>
  );
}
